import { CheckIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

// Client-facing fulfilment lifecycle:
//   Placed → Received by factory → Packed → Dispatched → Delivered
// Production/hold/proof all sit under the "Placed" node until the factory
// receives the run. Cancelled orders short-circuit to a muted note.

type Stage = { key: string; label: string };
const STAGES: Stage[] = [
  { key: "placed", label: "Placed" },
  { key: "factory_received", label: "Received by factory" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Dispatched" },
  { key: "delivered", label: "Delivered" },
];

// Which node the given order status has reached.
const STAGE_INDEX: Record<string, number> = {
  draft: 0,
  proof: 0,
  hold: 0,
  production: 0,
  factory_received: 1,
  packed: 2,
  shipped: 3,
  delivered: 4,
};

export type FulfilmentTimestamps = {
  placedAt?: string | null;
  factoryReceivedAt?: string | null;
  packedAt?: string | null;
  dispatchedAt?: string | null;
};

const fmt = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : null;

export function FulfilmentTimeline({
  status,
  royalMailTracking,
  timestamps,
  className,
}: {
  status: string;
  royalMailTracking?: string | null;
  timestamps?: FulfilmentTimestamps;
  className?: string;
}) {
  if (status === "cancelled") {
    return (
      <div
        className={cn(
          "rounded-md border-l-[3px] border-[var(--danger)] bg-[#FFF8F7] px-4 py-3 text-sm text-[color:var(--danger-fg)]",
          className,
        )}
      >
        This order was cancelled and will not be fulfilled.
      </div>
    );
  }

  const current = STAGE_INDEX[status] ?? 0;
  const when: Array<string | null> = [
    fmt(timestamps?.placedAt),
    fmt(timestamps?.factoryReceivedAt),
    fmt(timestamps?.packedAt),
    fmt(timestamps?.dispatchedAt),
    null,
  ];
  const dispatched = current >= 3;

  return (
    <div className={className}>
      <ol className="flex list-none items-start gap-0 p-0">
        {STAGES.map((stage, i) => {
          const done = i < current;
          const active = i === current;
          const reached = i <= current;
          return (
            <li key={stage.key} className="relative flex min-w-0 flex-1 flex-col items-center">
              {/* Connector to the previous node */}
              {i > 0 && (
                <span
                  aria-hidden
                  className="absolute right-1/2 top-3 h-[2px] w-full"
                  style={{ background: i <= current ? "var(--studio)" : "var(--border-1)" }}
                />
              )}
              <span
                className={cn(
                  "relative z-[1] grid h-6 w-6 place-items-center rounded-full text-2xs font-bold",
                  reached ? "text-white" : "bg-bg-2 text-fg-3",
                )}
                style={reached ? { background: "var(--studio)" } : undefined}
              >
                {done ? <CheckIcon width={12} height={12} strokeWidth={2.5} /> : i + 1}
              </span>
              <span
                className={cn(
                  "mt-1.5 text-center text-xxs leading-tight",
                  active ? "font-semibold text-fg-1" : reached ? "text-fg-2" : "text-fg-3",
                )}
              >
                {stage.label}
              </span>
              {when[i] && (
                <span className="mt-0.5 text-center font-mono text-xxs tabular-nums text-fg-3">
                  {when[i]}
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {dispatched && royalMailTracking && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-[var(--border-1)] bg-bg-1 px-4 py-3">
          <span className="text-eyebrow font-semibold uppercase text-fg-mute">
            Royal Mail return tracking
          </span>
          <code className="rounded bg-white px-2 py-1 font-mono text-sm font-semibold tabular-nums text-fg-1">
            {royalMailTracking}
          </code>
        </div>
      )}
    </div>
  );
}
